import { useEffect, useState } from "react";
import { api } from "./api";
import { BrandMark, Icon } from "./components";
import type { BackendConfig, Session } from "./types";

interface LoginProps {
  onAuthed: (session: Session, baseUrl: string) => void;
  bootstrapUrl: string;
}

export function Login({ onAuthed, bootstrapUrl }: LoginProps) {
  const [backends, setBackends] = useState<BackendConfig[]>([
    { id: "self", label: "this machine", url: bootstrapUrl, default: true },
  ]);
  const [backendId, setBackendId] = useState("self");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load backends list (so the user can pick which machine to sign into).
  useEffect(() => {
    let cancelled = false;
    api
      .backends(bootstrapUrl)
      .then((res) => {
        if (cancelled) return;
        if (res.backends.length > 0) {
          setBackends(res.backends);
          const def = res.backends.find((b) => b.default) ?? res.backends[0];
          if (def) setBackendId(def.id);
        }
      })
      .catch(() => {
        // No-op; user can still try the default URL.
      });
    return () => {
      cancelled = true;
    };
  }, [bootstrapUrl]);

  const activeBackend = backends.find((b) => b.id === backendId) ?? backends[0]!;
  // Same-origin shim: backends.json may declare url:"" (the production
  // same-origin case). Fall back to bootstrapUrl so dev (vite :5173 →
  // backend :8765) still works without baking dev-specific URLs into the
  // committed config.
  const effectiveUrl = activeBackend.url || bootstrapUrl;

  const submit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    // Read DOM values directly via FormData rather than React state so that
    // browser-autofilled values (which don't trigger onChange and leave the
    // controlled state at "") are picked up. Without this, clicking Sign In
    // is a silent no-op when the password was autofilled — exact symptom of
    // the 2026-05-19 production bug ("click does nothing, no network call").
    const formData = new FormData(e.currentTarget);
    const u = ((formData.get("username") as string | null) ?? username).trim();
    const p = (formData.get("password") as string | null) ?? password;
    if (!u || !p) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await api.login(effectiveUrl, u, p, backendId);
      onAuthed(
        {
          token: res.token,
          user: res.user,
          backend_id: backendId,
          started_at: new Date().toISOString(),
        },
        effectiveUrl,
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-card">
        <div className="login-brand">
          <BrandMark />
          <span>orchestrator</span>
        </div>
        <div className="login-title">Sign in to your fleet</div>
        <div className="login-sub">
          One operator per machine. Pick the backend, sign in, and the dashboard starts streaming from there.
        </div>

        <form onSubmit={submit} className="login-form">
          <label className="login-field">
            <span>Backend</span>
            <select value={backendId} onChange={(e) => setBackendId(e.target.value)} disabled={submitting}>
              {backends.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.label} — {b.url}
                </option>
              ))}
            </select>
          </label>

          <label className="login-field">
            <span>Username</span>
            <input
              type="text"
              name="username"
              autoFocus
              autoComplete="username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="harsh"
              disabled={submitting}
            />
          </label>

          <label className="login-field">
            <span>Password</span>
            <div className="login-password">
              <input
                type={showPass ? "text" : "password"}
                name="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={submitting}
              />
              <button
                type="button"
                className="btn btn-xs btn-ghost"
                onClick={() => setShowPass((s) => !s)}
                tabIndex={-1}
                aria-label={showPass ? "Hide password" : "Show password"}
              >
                {showPass ? <Icon.EyeOff /> : <Icon.Eye />}
              </button>
            </div>
          </label>

          {error && <div className="login-error">{error}</div>}

          <button
            type="submit"
            className="btn btn-primary"
            disabled={submitting}
            style={{ width: "100%", justifyContent: "center", padding: "10px 12px" }}
          >
            <Icon.Lock /> {submitting ? "Signing in…" : "Sign in"}
          </button>
        </form>

        <div className="login-foot">
          <b style={{ color: "var(--fg-1)" }}>Strict mode (public).</b> Credentials are verified server-side against an
          Argon2id-hashed user store; the dashboard receives an HS256 JWT and sends it on every request. Requests
          arriving via nginx (this page) require a valid token. Local workers + main agent on the same machine still run
          without a token — that'll change when worker boot prompts learn to mint their own. Operator manages users via{" "}
          <span className="mono">scripts/manage_users.py</span>.
        </div>
      </div>
    </div>
  );
}
